import React, { useCallback, useMemo, forwardRef } from 'react';
import ContentSection from '@/pages/Products/VpsOciDetailPage/components/ContentSection';
import useLocale from '@/hooks/useLocale';
import * as Styles from './Management.styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  IconChevron,
} from 'gatorcomponents';
import { locale } from '@/utils/locale';
import { formatTimestampDate } from '@/utils/Formatters/formatTimestampDate';
import { useParams } from 'react-router-dom';
import { productsActions } from '@/redux/modules';
import { getIconBySlug } from '@/pages/Products/VpsOciDetailPage/utils/getIconBySlug';
import { backupAnalytics } from '@/analytics';

import { ENABLE_VPS_OCI_MANAGEMENT_SO_APPS } from '@/config/GrowthBook/constants';
import { useFeatureIsOn } from '@growthbook/growthbook-react';

const Management = forwardRef((props, ref) => {
  const { setShowProductTemplatesPage } = props;
  const dispatch = useDispatch();
  const vpsOci = useSelector(state => state.vpsOci);
  const {
    management, addons: addonsList, ssh, vps,
  } = (vpsOci && vpsOci.data) || {};
  const { serverIp } = ssh || {};
  const { domain } = vps || {};
  const enableVpsOciManagementSoApps = useFeatureIsOn(ENABLE_VPS_OCI_MANAGEMENT_SO_APPS);

  const n8nProducts = useSelector(state => state.products);
  const { n8n: n8nState } = n8nProducts || {};
  const { id: productId } = useParams();

  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: managementLocale } = productDetailsLocale;

  const soAppsRedirect = useCallback(() => {
    setShowProductTemplatesPage(true);
  }, [setShowProductTemplatesPage]);

  const backupRedirect = useCallback(() => {
    const url = `${locale('routes.products')}${locale('routes.productdetail')}/${productId}${locale('routes.backup')}`;
    backupAnalytics.backupButtonClick();
    window.open(url, '_blank');
  }, [productId]);

  const snapshotRedirect = useCallback(() => {
    const url = `${locale('routes.products')}${locale('routes.productdetail')}/${productId}${locale('routes.snapshots')}`;
    window.open(url, '_blank');
  }, [productId]);

  const sshKeysRedirect = useCallback(() => {
    const url = `${locale('routes.products')}${locale('routes.productdetail')}/${productId}${locale('routes.sshKeys')}`;
    window.open(url, '_blank');
  }, [productId]);

  const cpanelRedirect = useCallback(() => {
    const url = `${locale('routes.whmRedirectAccess')}/${productId}`;
    window.open(url, '_blank');
  }, [productId]);

  // Generic helper functions
  const openUrl = useCallback((url) => {
    window.open(url, '_blank');
  }, []);

  const openServerIpUrl = useCallback((suffix = '') => {
    if (!serverIp) return;
    const url = `http://${serverIp}${suffix}`;
    openUrl(url);
  }, [serverIp, openUrl]);

  const openServerIpWithPort = useCallback((port) => {
    if (!serverIp) return;
    const url = `http://${serverIp}:${port}`;
    openUrl(url);
  }, [serverIp, openUrl]);

  const openDomainUrl = useCallback(() => {
    if (!domain) return;
    let url = domain.includes('https://') ? domain : `https://${domain}`;
    if (!url.includes('www.')) {
      url = url.replace('https://', 'https://www.');
    }
    openUrl(url);
  }, [domain, openUrl]);

  const n8nRedirect = useCallback(() => {
    if (!n8nState || !productsActions || !productsActions.n8n) return;

    const openN8N = (ip) => {
      const url = `http://${ip}`;
      window.open(url, '_blank');
    };

    if (n8nState.data && n8nState.hostingId === productId) {
      openN8N(n8nState.data.ip);
    } else {
      dispatch(productsActions.n8n.request({ hostingId: productId, actionType: 'open_n8n', callback: openN8N }));
    }
  }, [n8nState, productId, dispatch]);

  const pleskRedirect = useCallback(() => {
    openServerIpUrl('/admin');
  }, [openServerIpUrl]);

  const wordpressRedirect = useCallback(() => {
    openDomainUrl();
  }, [openDomainUrl]);

  const dockerRedirect = useCallback(() => {
    openServerIpUrl();
  }, [openServerIpUrl]);

  const moodleRedirect = useCallback(() => {
    openServerIpUrl();
  }, [openServerIpUrl]);

  const nodejsRedirect = useCallback(() => {
    openServerIpUrl();
  }, [openServerIpUrl]);

  const woocommerceRedirect = useCallback(() => {
    openDomainUrl();
  }, [openDomainUrl]);

  const pterodactylRedirect = useCallback(() => {
    openServerIpUrl();
  }, [openServerIpUrl]);

  const laravelRedirect = useCallback(() => {
    openServerIpUrl();
  }, [openServerIpUrl]);

  const cyberPanelRedirect = useCallback(() => {
    openServerIpWithPort(8090);
  }, [openServerIpWithPort]);

  const magentoRedirect = useCallback(() => {
    openDomainUrl();
  }, [openDomainUrl]);

  const supabaseRedirect = useCallback(() => {
    openServerIpWithPort(8000);
  }, [openServerIpWithPort]);

  const kubernetesRedirect = useCallback(() => {
    openServerIpUrl('.sslip.io');
  }, [openServerIpUrl]);

  const easyPanelRedirect = useCallback(() => {
    openServerIpWithPort(3000);
  }, [openServerIpWithPort]);

  const webminRedirect = useCallback(() => {
    openServerIpWithPort(10000);
  }, [openServerIpWithPort]);

  const virtualminRedirect = useCallback(() => {
    openServerIpWithPort(10000);
  }, [openServerIpWithPort]);

  const openClawRedirect = useCallback(() => {
    openServerIpWithPort(47716);
  }, [openServerIpWithPort]);

  const checkExistSlug = useCallback(slug => !slug || slug === '' || managementLocale.management.items.findIndex(itemLocale => itemLocale.slug === slug) === -1, [managementLocale.management.items]);

  const getNameItem = useCallback((slug) => {
    if (!slug) return null;
    const item = managementLocale.management.items.find(itemLocale => itemLocale.slug === slug);
    return item ? item.name : '';
  }, [managementLocale.management.items]);

  const getInfoItem = useCallback((item) => {
    if (!item) return null;

    const { slug, info } = item;
    if (!slug && !info) return null;

    if (slug === 'so-apps') {
      const { version } = item;
      const name = (managementLocale.store.details.operationalSystems[info] && managementLocale.store.details.operationalSystems[info].name) || item.info;
      return name ? `${name}${version ? ` ${version}` : ''}` : '';
    }

    if (slug === 'backup' || slug === 'snapshot') {
      const { date } = item;
      if (!date) return info;
      return `${managementLocale.management.items.find(i => i.slug === slug).info} ${formatTimestampDate(date)}`;
    }

    return info;
  }, [managementLocale.store.details.operationalSystems, managementLocale.management.items]);

  const slugHandlers = useMemo(() => ({
    cpanel: cpanelRedirect,
    n8n: n8nRedirect,
    plesk: pleskRedirect,
    'wordpress-1-clique': wordpressRedirect,
    'wordpress-openlitespeed': wordpressRedirect,
    wordpress: wordpressRedirect,
    docker: dockerRedirect,
    moodle: moodleRedirect,
    'node-js': nodejsRedirect,
    woocommerce: woocommerceRedirect,
    pterodactyl: pterodactylRedirect,
    laravel: laravelRedirect,
    cyberpanel: cyberPanelRedirect,
    magento: magentoRedirect,
    supabase: supabaseRedirect,
    'kubernetes-k3s': kubernetesRedirect,
    easypanel: easyPanelRedirect,
    webmin: webminRedirect,
    virtualmin: virtualminRedirect,
    'so-apps': soAppsRedirect,
    backup: backupRedirect,
    snapshot: snapshotRedirect,
    'ssh-keys': sshKeysRedirect,
    openclaw: openClawRedirect,
  }), [soAppsRedirect, backupRedirect, snapshotRedirect, sshKeysRedirect, n8nRedirect, cpanelRedirect, dockerRedirect, moodleRedirect, nodejsRedirect, woocommerceRedirect, pterodactylRedirect, laravelRedirect, cyberPanelRedirect, magentoRedirect, supabaseRedirect, kubernetesRedirect, easyPanelRedirect, webminRedirect, virtualminRedirect, pleskRedirect, wordpressRedirect, openClawRedirect]);

  const handleClick = useCallback((item) => {
    if (!item) return;

    const handler = slugHandlers[item.slug];
    if (handler) {
      handler();
    }
  }, [slugHandlers]);

  const hasHandler = useCallback((item) => {
    if (!item) return false;

    return !!slugHandlers[item.slug];
  }, [slugHandlers]);

  if ((!management || management.length === 0) && (!addonsList || addonsList.length === 0)) return null;

  let managementList = [
    ...(management || []),
    ...(addonsList || []),
  ];

  if (!enableVpsOciManagementSoApps) {
    managementList = managementList.filter(item => item.slug !== 'so-apps');
  }

  if (managementList.length === 0) return null;

  return (
    <ContentSection
      ref={ref}
      direction="column"
      align="flex-start"
      justify="flex-start"
      title={managementLocale.management.title.text}
      tooltip={managementLocale.management.title.tooltip}
      testId="vps-management-section"
    >
      <Styles.ListItems>
        {managementList
          .filter(item => !checkExistSlug(item.slug))
          .map((item) => {
            const iconItem = getIconBySlug(item.slug);
            const itemName = getNameItem(item.slug);
            const itemInfo = getInfoItem(item);
            const itemHasHandler = hasHandler(item);

            return (
              <Styles.Item
                key={item.slug}
                onClick={itemHasHandler ? () => handleClick(item) : undefined}
                $itemHasHandler={itemHasHandler}
                data-testid={`vps-management-item-${item.slug}`}
              >
                {iconItem && <Styles.ItemIcon src={iconItem} alt={item.slug} data-testid={`vpsoci-management-icon-${item.slug}`} />}
                <Styles.ItemContent>
                  {itemName && <Styles.ItemName data-testid="vps-management-name">{itemName}</Styles.ItemName>}
                  {itemInfo && <Styles.ItemInfo data-testid="vps-management-info">{itemInfo}</Styles.ItemInfo>}
                </Styles.ItemContent>
                {itemHasHandler && (
                  <Styles.ItemArrow>
                    <IconChevron color="primaryMedium" size="lg" direction="right" />
                  </Styles.ItemArrow>
                )}
              </Styles.Item>
            );
          })}
      </Styles.ListItems>
    </ContentSection>
  );
});

Management.displayName = 'Management';

export default Management;
